import axios from 'axios';

// Global flag to prevent duplicate session expired modals
let isSessionExpiredModalOpen = false;
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

/**
 * Attempt to refresh the auth token via the backend cookie-based refresh endpoint.
 * The cookie is sent automatically because withCredentials is enabled globally.
 */
async function tryRefreshToken(): Promise<string | null> {
  try {
    const response = await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true });
    return response.data.token;
  } catch {
    return null;
  }
}

/**
 * Set up global axios response interceptor to handle session expiration
 * - Detects HTTP 401 responses
 * - Attempts token refresh once
 * - Retries original request after successful refresh
 * - Dispatches 'session-expired' event if refresh fails
 */
export function setupAxiosInterceptors() {
  // Request interceptor: inject dev token for testing if present
  axios.interceptors.request.use((config) => {
    const devToken = localStorage.getItem('dev_token');
    if (devToken) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${devToken}`;
    }
    return config;
  });

  // Response interceptor
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Check if this is an HTTP 401 error
      if (error.response?.status === 401) {
        // Check if user was previously authenticated (cookie-based)
        const wasAuthenticated = localStorage.getItem('auth_present') === 'true';

        if (!wasAuthenticated) {
          // User wasn't authenticated - silent redirect
          window.location.href = '/admin/login';
          return Promise.reject(error);
        }

        // Avoid infinite loops: if this request is the refresh itself, fail immediately
        if (originalRequest.url?.includes('/auth/refresh')) {
          if (!isSessionExpiredModalOpen) {
            isSessionExpiredModalOpen = true;
            window.dispatchEvent(new CustomEvent('session-expired'));
          }
          return Promise.reject(error);
        }

        // Attempt token refresh
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const newToken = await tryRefreshToken();
            if (newToken) {
              onRefreshed(newToken);
              isRefreshing = false;
              // Retry the original request
              return axios(originalRequest);
            } else {
              // Refresh failed
              isRefreshing = false;
              if (!isSessionExpiredModalOpen) {
                isSessionExpiredModalOpen = true;
                window.dispatchEvent(new CustomEvent('session-expired'));
              }
              return Promise.reject(error);
            }
          } catch {
            isRefreshing = false;
            if (!isSessionExpiredModalOpen) {
              isSessionExpiredModalOpen = true;
              window.dispatchEvent(new CustomEvent('session-expired'));
            }
            return Promise.reject(error);
          }
        } else {
          // Another request is already refreshing the token
          // Queue this request to retry after refresh completes
          return new Promise((resolve) => {
            addRefreshSubscriber((newToken: string) => {
              resolve(axios(originalRequest));
            });
          });
        }
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Reset the session expired modal flag
 * Call this after modal is closed
 */
export function resetSessionExpiredFlag() {
  isSessionExpiredModalOpen = false;
}
